import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Download, ArrowRightLeft, Shuffle, Power, Trash2, ShieldCheck, Clock, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';

// 3.30 — MigrationStorySection: dedicated section "Switch da Yoast in 3 minuti"
// Promotes the Migration Wizard from a single card to a hero use case
const MigrationStorySection = () => {
  const { t } = useTranslation();

  const steps = [
    { id: 'scan',      icon: Search,         color: '#0055FF' },
    { id: 'backup',    icon: Download,       color: '#06B6D4' },
    { id: 'migrate',   icon: ArrowRightLeft, color: '#10B981' },
    { id: 'redirects', icon: Shuffle,        color: '#8B5CF6' },
    { id: 'activate',  icon: Power,          color: '#F59E0B' },
    { id: 'cleanup',   icon: Trash2,         color: '#EC4899' },
  ];

  const safety = ['noLoss', 'noUrl', 'noRanking'];

  return (
    <section className="py-24 px-6 relative overflow-hidden" data-testid="migration-story-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a1410] to-[#050505]" />
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#10B981]/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#0055FF]/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 mb-6">
            <Rocket className="w-4 h-4 text-[#10B981]" />
            <span className="text-sm text-[#6ee7b7]">{t('migrationStory.badge')}</span>
          </div>

          <h2 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-6">
            {t('migrationStory.title1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#00E5FF]">
              {t('migrationStory.title2')}
            </span>
          </h2>

          <p className="text-lg text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed">
            {t('migrationStory.subtitle')}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm">
            <div className="inline-flex items-center gap-2 text-[#d4d4d8]">
              <Clock className="w-4 h-4 text-[#10B981]" />
              <span>{t('migrationStory.metric1')}</span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-white/20" />
            <div className="inline-flex items-center gap-2 text-[#d4d4d8]">
              <ShieldCheck className="w-4 h-4 text-[#10B981]" />
              <span>{t('migrationStory.metric2')}</span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-white/20" />
            <div className="inline-flex items-center gap-2 text-[#d4d4d8]">
              <Rocket className="w-4 h-4 text-[#10B981]" />
              <span>{t('migrationStory.metric3')}</span>
            </div>
          </div>
        </div>

        {/* 6 step grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-14">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                className="relative rounded-2xl p-5 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-all animate-fade-up group"
                style={{ animationDelay: `${idx * 0.07}s` }}
                data-testid={`migration-step-${idx}`}
              >
                <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-[#0a0a0a] border border-white/20 flex items-center justify-center text-xs font-bold text-white/80">
                  {idx + 1}
                </div>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 mt-1"
                  style={{ background: `${step.color}1a`, border: `1px solid ${step.color}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: step.color }} />
                </div>
                <h4 className="text-sm font-semibold text-white mb-1.5">
                  {t(`migrationStory.steps.${step.id}.title`)}
                </h4>
                <p className="text-xs text-[#A1A1AA] leading-relaxed">
                  {t(`migrationStory.steps.${step.id}.desc`)}
                </p>
              </div>
            );
          })}
        </div>

        {/* Safety reassurance bar */}
        <div className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-6 mb-12 animate-fade-up">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#10B981]" />
            </div>
            <div>
              <h3 className="text-lg font-['Outfit'] font-medium text-white">
                {t('migrationStory.safetyTitle')}
              </h3>
              <p className="text-sm text-[#A1A1AA] mt-1">
                {t('migrationStory.safetySubtitle')}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            {safety.map(s => (
              <div key={s} className="flex items-start gap-2.5 text-sm">
                <div className="w-5 h-5 rounded-full bg-[#10B981]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#10B981] text-xs font-bold">{'✓'}</span>
                </div>
                <div>
                  <div className="text-white font-medium">{t(`migrationStory.safety.${s}.title`)}</div>
                  <div className="text-xs text-[#A1A1AA] mt-0.5">{t(`migrationStory.safety.${s}.desc`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-up">
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register">
              <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-black font-semibold transition-transform hover:scale-105">
                <Rocket className="w-4 h-4" />
                {t('migrationStory.ctaPrimary')}
              </button>
            </Link>
            <Link to="/guide/migrazione-da-yoast">
              <button className="px-8 py-3 rounded-full border border-white/20 text-white hover:bg-white/5 font-medium transition-colors">
                {t('migrationStory.ctaSecondary')}
              </button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#71717A]">
            {t('migrationStory.ctaNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default MigrationStorySection;
